import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
// lenis styles (optional — prevents FOUC on some setups)
// import "lenis/dist/lenis.css";
import Scene3D, { scrollState } from "../components/three/Scene3D";
import WaitlistForm from "../components/ui/WaitlistForm";
import { getWaitlistStats } from "../lib/supabase";

// ── Live waitlist count ────────────────────────────────────────────────────────
function useWaitlistCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    getWaitlistStats().then(({ total }) => {
      if (total > 0) setCount(total);
    });
  }, []);
  // Format: 1200 → "1,200+"
  if (count === null) return "1,200+";
  return count.toLocaleString() + "+";
}

gsap.registerPlugin(ScrollTrigger);

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      "#04060E",
  bg2:     "#060A14",
  green:   "#10B77F",
  blue:    "#627EEA",
  purple:  "#A78BFA",
  gold:    "#F59E0B",
  text:    "#EEF0F6",
  muted:   "rgba(238,240,246,0.44)",
  border:  "rgba(255,255,255,0.07)",
  font:    "'Space Grotesk', sans-serif",
};

// ── Framer variants ───────────────────────────────────────────────────────────
const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};
const fadeLeft = {
  hidden:  { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

// ── Loading screen ────────────────────────────────────────────────────────────
function LoadingScreen({ onDone }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      const step = current < 60 ? 3.5 : current < 85 ? 1.4 : 0.6;
      current = Math.min(current + step + Math.random() * 1.5, 100);
      setPct(Math.round(current));
      if (current >= 100) {
        clearInterval(interval);
        setTimeout(onDone, 520);
      }
    }, 42);
    return () => clearInterval(interval);
  }, [onDone]);

  return (
    <motion.div
      exit={{
        clipPath: "inset(100% 0 0 0)",
        transition: { duration: 0.85, ease: [0.76, 0, 0.24, 1] },
      }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: C.bg,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        clipPath: "inset(0 0 0 0)",
      }}
    >
      {/* Logo mark */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ marginBottom: 56 }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
          <span style={{
            fontFamily: C.font, fontWeight: 900, fontSize: "2.2rem",
            color: C.text, letterSpacing: "-0.04em",
          }}>Alpha</span>
          <span style={{
            fontFamily: C.font, fontWeight: 900, fontSize: "2.2rem",
            color: C.green, letterSpacing: "-0.04em",
          }}>Stack</span>
        </div>
        <div style={{
          height: 2, background: `linear-gradient(90deg, transparent, ${C.green}, transparent)`,
          marginTop: 8, opacity: 0.6,
        }} />
      </motion.div>

      {/* Progress bar */}
      <div style={{ width: 220, marginBottom: 14 }}>
        <div style={{
          height: 1, background: "rgba(255,255,255,0.08)",
          borderRadius: 1, overflow: "hidden",
        }}>
          <motion.div
            style={{
              height: "100%",
              background: `linear-gradient(90deg, ${C.green}80, ${C.green})`,
              boxShadow: `0 0 12px ${C.green}`,
            }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.08 }}
          />
        </div>
      </div>

      {/* Counter */}
      <div style={{
        fontFamily: C.font, fontSize: "0.68rem", fontWeight: 700,
        letterSpacing: "0.2em", color: "rgba(255,255,255,0.22)",
        textTransform: "uppercase",
      }}>
        {pct < 100 ? `Initializing ${pct}%` : "Ready"}
      </div>

      {/* Bottom decorative line */}
      <div style={{
        position: "absolute", bottom: 32, left: "50%",
        transform: "translateX(-50%)",
        width: 48, height: 1,
        background: `rgba(16,183,127,0.25)`,
      }} />
    </motion.div>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { label: "Features", id: "features" },
    { label: "Why Join", id: "why"      },
  ];

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="p3-nav"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "0 32px",
        height: 60,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrolled ? "rgba(4,6,14,0.88)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? `1px solid ${C.border}` : "1px solid transparent",
        transition: "background 0.4s ease, border-color 0.4s ease, backdrop-filter 0.4s ease",
      }}
    >
      {/* Logo — same image as home page */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          src="/images/alphastack-logo-pVgok4VY (1).png"
          alt="AlphaStack"
          style={{ height: 32, objectFit: "contain" }}
        />
      </div>

      {/* Nav links */}
      <div className="p3-nav-links" style={{ display: "flex", gap: 36, alignItems: "center" }}>
        {links.map(({ label, id }) => (
          <NavLink key={id} targetId={id}>{label}</NavLink>
        ))}
        <AdminNavLink />
      </div>

      {/* CTA */}
      <motion.button
        whileHover={{ scale: 1.03, boxShadow: `0 0 24px ${C.green}44` }}
        whileTap={{ scale: 0.97 }}
        style={{
          fontFamily: C.font, fontSize: "0.8rem", fontWeight: 700,
          color: "#fff", background: C.green,
          border: "none", borderRadius: 8,
          padding: "8px 18px", cursor: "pointer",
          letterSpacing: "0.01em",
        }}
        onClick={() => document.getElementById("p3-cta")?.scrollIntoView({ behavior: "smooth" })}
      >
        Join Waitlist
      </motion.button>
    </motion.nav>
  );
}

function AdminNavLink() {
  const [hov, setHov] = useState(false);
  return (
    <a
      href="/admin"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ position: "relative", textDecoration: "none" }}
    >
      <span style={{
        fontFamily: C.font, fontSize: "0.82rem", fontWeight: 500,
        color: hov ? C.text : C.muted,
        transition: "color 0.2s",
        letterSpacing: "0.01em",
      }}>
        Admin
      </span>
      <motion.div
        style={{
          position: "absolute", bottom: -3, left: 0, right: 0,
          height: 1, background: C.green,
          originX: 0.5,
        }}
        animate={{ scaleX: hov ? 1 : 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />
    </a>
  );
}

function NavLink({ children, targetId }) {
  const [hov, setHov] = useState(false);
  const handleClick = () => {
    document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ position: "relative", cursor: "pointer" }}
    >
      <span style={{
        fontFamily: C.font, fontSize: "0.82rem", fontWeight: 500,
        color: hov ? C.text : C.muted,
        transition: "color 0.2s",
        letterSpacing: "0.01em",
      }}>
        {children}
      </span>
      <motion.div
        style={{
          position: "absolute", bottom: -3, left: 0, right: 0,
          height: 1, background: C.green,
          originX: 0.5,
        }}
        animate={{ scaleX: hov ? 1 : 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />
    </div>
  );
}

// ── Launch countdown ──────────────────────────────────────────────────────────
const LAUNCH_DATE = new Date("2026-04-14T00:00:00");

function useCountdown() {
  const calc = () => {
    const diff = LAUNCH_DATE - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function HeroCountdown() {
  const { d, h, m, s } = useCountdown();
  const units = [
    { v: d, label: "Days" },
    { v: h, label: "Hours" },
    { v: m, label: "Min" },
    { v: s, label: "Sec" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        marginBottom: 32,
        padding: "10px 18px",
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        backdropFilter: "blur(12px)",
      }}
    >
      <span style={{
        fontFamily: C.font, fontSize: "0.65rem", fontWeight: 700,
        letterSpacing: "0.16em", textTransform: "uppercase",
        color: C.muted, marginRight: 8,
      }}>
        Launch in
      </span>
      {units.map(({ v, label }, i) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            minWidth: 38,
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${C.green}20`,
            borderRadius: 8,
            padding: "5px 8px",
          }}>
            <span style={{
              fontFamily: C.font, fontWeight: 800, fontSize: "1.05rem",
              color: C.green, letterSpacing: "-0.02em", lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}>
              {String(v).padStart(2, "0")}
            </span>
            <span style={{
              fontFamily: C.font, fontSize: "0.52rem", fontWeight: 600,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: "rgba(238,240,246,0.3)", marginTop: 3,
            }}>
              {label}
            </span>
          </div>
          {i < units.length - 1 && (
            <span style={{
              fontFamily: C.font, fontWeight: 700, fontSize: "0.9rem",
              color: `${C.green}50`, marginBottom: 8,
            }}>:</span>
          )}
        </div>
      ))}
    </motion.div>
  );
}

// ── Hero section ──────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="p3-hero" style={{
      position: "relative", zIndex: 1,
      minHeight: "100vh",
      display: "flex", flexDirection: "column",
      justifyContent: "center",
      padding: "120px 48px 80px",
    }}>
      {/* Radial glow top */}
      <div style={{
        position: "absolute", top: -80, left: "30%",
        width: 800, height: 600, pointerEvents: "none",
        background: `radial-gradient(ellipse, ${C.green}18 0%, transparent 65%)`,
        filter: "blur(60px)",
      }} />

      <div style={{ maxWidth: 1100, width: "100%", margin: "0 auto" }}>

        {/* Badge — slightly off-center */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "5px 14px 5px 10px",
            border: `1px solid ${C.green}28`,
            borderRadius: 999, marginBottom: 20,
            background: `${C.green}0A`,
          }}
        >
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: C.green, boxShadow: `0 0 8px ${C.green}`,
            animation: "pulseGold 2s ease infinite",
          }} />
          <span style={{
            fontFamily: C.font, fontSize: "0.73rem", fontWeight: 600,
            color: C.green, letterSpacing: "0.04em",
          }}>
            Early Access Now Open
          </span>
        </motion.div>

        {/* Headline — asymmetric, large */}
        <div style={{ marginBottom: 28, maxWidth: 720 }}>
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.1 }}
            style={{ fontFamily: C.font, margin: 0 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              style={{
                display: "block",
                fontSize: "clamp(3.2rem, 6.8vw, 5.5rem)",
                fontWeight: 400, letterSpacing: "-0.04em", lineHeight: 1.02,
                color: C.text,
              }}
            >
              Your portfolio,
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              style={{
                display: "block",
                fontSize: "clamp(3.2rem, 6.8vw, 5.5rem)",
                fontWeight: 900, letterSpacing: "-0.05em", lineHeight: 1.02,
                color: C.text, marginLeft: 4,
              }}
            >
              Built to{" "}
              <span style={{
                background: `linear-gradient(135deg, ${C.green} 0%, #6EE7B7 60%, ${C.green} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                win.
              </span>
            </motion.span>
          </motion.h1>
        </div>

        {/* Two-column: description + form */}
        <div className="p3-hero-grid" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "clamp(32px, 5vw, 72px)",
          alignItems: "start",
          maxWidth: 860,
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p style={{
              fontFamily: C.font, fontSize: "1.06rem",
              color: C.muted, lineHeight: 1.78, margin: "0 0 32px",
            }}>
              Alpha Stack is the AI investment platform built to beat the market. Powered by decades of backtested data, our statistically engineered portfolios are designed to generate positive alpha vs the S&P 500 across every market cycle. bull, bear, and everything in between. Real-time signal intelligence, active management recommendations, and institutional-grade portfolio analytics, all in one place. Built for the self-directed investor who refuses to settle for average returns.
            </p>

            {/* Trust indicators */}
            

            {/* Countdown */}
            <div style={{ marginTop: 28 }}>
              <HeroCountdown />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.74, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div style={{
              background: "rgba(255,255,255,0.025)",
              border: `1px solid ${C.border}`,
              borderRadius: 20, padding: "28px 24px",
              backdropFilter: "blur(16px)",
            }}>
              <div style={{
                fontFamily: C.font, fontSize: "0.7rem", fontWeight: 700,
                letterSpacing: "0.14em", textTransform: "uppercase",
                color: C.green, marginBottom: 16,
              }}>
                Join the waitlist
              </div>
              <WaitlistForm variant="hero" />
              <p style={{
                fontFamily: C.font, fontSize: "0.72rem", color: "rgba(238,240,246,0.28)",
                marginTop: 14, lineHeight: 1.5,
              }}>
                Be among the first to access Alpha Stack when we launch. Free — no card needed.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="p3-scroll-cue"
          style={{
            position: "absolute", bottom: 36, left: 48,
            display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <div style={{
            width: 24, height: 38, borderRadius: 12,
            border: `1px solid ${C.green}30`,
            display: "flex", alignItems: "flex-start",
            justifyContent: "center", padding: "5px 0",
          }}>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: 4, height: 8, borderRadius: 2, background: C.green,
              }}
            />
          </div>
          <span style={{
            fontFamily: C.font, fontSize: "0.6rem", fontWeight: 600,
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: "rgba(238,240,246,0.28)",
          }}>
            Scroll to explore
          </span>
        </motion.div>
      </div>
    </section>
  );
}

// ── Feature card ──────────────────────────────────────────────────────────────
function FeatureCard({ number, accent, title, desc, mini, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [hov, setHov] = useState(false);

  return (
    <motion.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.022)",
        border: `1px solid ${hov ? accent + "44" : C.border}`,
        borderRadius: 22, padding: "32px 28px",
        position: "relative", overflow: "hidden",
        cursor: "default",
        boxShadow: hov ? `0 0 60px ${accent}14, inset 0 1px 0 rgba(255,255,255,0.08)` : "inset 0 1px 0 rgba(255,255,255,0.04)",
        transition: "border-color 0.35s, background 0.35s, box-shadow 0.35s",
        transform: hov ? "translateY(-5px)" : "translateY(0)",
      }}
    >
      {/* Top edge highlight */}
      <div style={{
        position: "absolute", top: 0, left: "15%", right: "15%", height: 1,
        background: `linear-gradient(90deg, transparent, ${accent}55, transparent)`,
        opacity: hov ? 1 : 0.4,
        transition: "opacity 0.35s",
      }} />

      {/* Number */}
      <div style={{
        fontFamily: C.font, fontSize: "0.65rem", fontWeight: 700,
        letterSpacing: "0.2em", color: accent, opacity: 0.8,
        marginBottom: 20,
      }}>
        {number}
      </div>

      <h3 style={{
        fontFamily: C.font, fontSize: "1.12rem", fontWeight: 700,
        color: C.text, lineHeight: 1.32, margin: "0 0 12px",
        letterSpacing: "-0.02em",
      }}>
        {title}
      </h3>

      <p style={{
        fontFamily: C.font, fontSize: "0.87rem", color: C.muted,
        lineHeight: 1.72, margin: "0 0 24px",
      }}>
        {desc}
      </p>

      {/* Mini UI */}
      {mini(hov)}
    </motion.div>
  );
}

// ── Feature mini UIs ──────────────────────────────────────────────────────────

// 1) AI Portfolio Generator — alpha line chart + health ring + action feed
function PortfolioGenMini({ hov }) {
  const pts = [12, 18, 14, 22, 19, 28, 24, 34, 30, 38, 35, 44];
  const spyPts = [12, 14, 13, 16, 15, 18, 17, 20, 19, 22, 21, 23];
  const W = 180, H = 52;
  const toX = (i) => (i / (pts.length - 1)) * W;
  const toY = (v, arr) => H - ((v - Math.min(...arr)) / (Math.max(...arr) - Math.min(...arr))) * H;
  const path = pts.map((v, i) => `${i === 0 ? "M" : "L"}${toX(i)},${toY(v, pts)}`).join(" ");
  const spy  = spyPts.map((v, i) => `${i === 0 ? "M" : "L"}${toX(i)},${toY(v, pts)}`).join(" ");

  const actions = [
    { text: "Rebalance AAPL → +2.4%", c: C.green },
    { text: "Trim TSLA exposure",      c: C.gold  },
    { text: "Add MSFT hedge",          c: "#60A5FA" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Chart */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontFamily: C.font, fontSize: "0.58rem", color: C.muted }}>Alpha vs SPY · 3Y</span>
          <span style={{ fontFamily: C.font, fontSize: "0.68rem", fontWeight: 800, color: C.green }}>+18.4%</span>
        </div>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          <path d={spy}  fill="none" stroke="#60A5FA" strokeWidth="1.2" strokeDasharray="3 2" opacity="0.45" />
          <path d={path} fill="none" stroke={C.green} strokeWidth="2"   strokeLinecap="round" opacity={hov ? 1 : 0.5} style={{ transition: "opacity 0.4s" }} />
          <defs>
            <linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.green} stopOpacity="0.18" />
              <stop offset="100%" stopColor={C.green} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${path} L${W},${H} L0,${H}Z`} fill="url(#ag1)" />
        </svg>
        <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
          <span style={{ fontFamily: C.font, fontSize: "0.55rem", color: "#60A5FA" }}>— SPY</span>
          <span style={{ fontFamily: C.font, fontSize: "0.55rem", color: C.green }}>— Portfolio</span>
        </div>
      </div>
      {/* Action feed */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px" }}>
        <div style={{ fontFamily: C.font, fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase", marginBottom: 7 }}>Active Management</div>
        {actions.map(({ text, c }, i) => (
          <div key={text} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "4px 0",
            borderBottom: i < actions.length - 1 ? `1px solid ${C.border}` : "none",
            opacity: hov ? 1 : 0.4,
            transition: `opacity 0.3s ease ${i * 0.08}s`,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: c, flexShrink: 0 }} />
            <span style={{ fontFamily: C.font, fontSize: "0.65rem", color: C.text }}>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// 2) Brokerage Integration — current vs ideal diff + alignment score
const ALLOC = [
  { label: "AAPL",  current: 28, ideal: 18, c: "#60A5FA" },
  { label: "TSLA",  current: 22, ideal: 10, c: "#F87171" },
  { label: "MSFT",  current: 14, ideal: 22, c: C.green   },
  { label: "Bonds", current:  8, ideal: 18, c: "#FBBF24" },
];

function BrokerageMini({ hov }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Diff bars */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontFamily: C.font, fontSize: "0.58rem", color: C.muted }}>Current vs Ideal</span>
          <div style={{ display: "flex", gap: 8 }}>
            <span style={{ fontFamily: C.font, fontSize: "0.52rem", color: "rgba(255,255,255,0.3)" }}>■ Current</span>
            <span style={{ fontFamily: C.font, fontSize: "0.52rem", color: C.green }}>■ Ideal</span>
          </div>
        </div>
        {ALLOC.map(({ label, current, ideal, c }, i) => (
          <div key={label} style={{ marginBottom: i < ALLOC.length - 1 ? 7 : 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontFamily: C.font, fontSize: "0.62rem", color: C.muted }}>{label}</span>
              <span style={{ fontFamily: C.font, fontSize: "0.62rem", fontWeight: 700, color: current > ideal ? "#F87171" : C.green }}>
                {current > ideal ? "▼" : "▲"} {Math.abs(current - ideal)}%
              </span>
            </div>
            <div style={{ position: "relative", height: 4, borderRadius: 2, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
              <motion.div animate={{ width: hov ? `${current}%` : "0%" }} transition={{ duration: 0.7, delay: i * 0.07 }}
                style={{ position: "absolute", height: "100%", background: `${c}55`, borderRadius: 2 }} />
              <motion.div animate={{ width: hov ? `${ideal}%` : "0%" }} transition={{ duration: 0.7, delay: i * 0.07 + 0.15 }}
                style={{ position: "absolute", height: "100%", background: C.green, borderRadius: 2, opacity: 0.7 }} />
            </div>
          </div>
        ))}
      </div>
      {/* Alignment score */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 14px", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
          <svg viewBox="0 0 40 40" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
            <motion.circle cx="20" cy="20" r="16" fill="none" stroke={C.green} strokeWidth="4"
              strokeLinecap="round" strokeDasharray="100.5"
              animate={{ strokeDashoffset: hov ? 100.5 * (1 - 0.72) : 100.5 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: C.font, fontSize: "0.6rem", fontWeight: 800, color: C.green }}>72</div>
        </div>
        <div>
          <div style={{ fontFamily: C.font, fontSize: "0.62rem", fontWeight: 700, color: C.text }}>Alignment Score</div>
          <div style={{ fontFamily: C.font, fontSize: "0.58rem", color: C.muted }}>3 gaps identified</div>
        </div>
      </div>
    </div>
  );
}

// 3) AI Article Intelligence — news impact + exposure + action
function ArticleMini({ hov }) {
  const impacts = [
    { asset: "AAPL", effect: "−2.1%", severity: "high",   c: "#F87171" },
    { asset: "MSFT", effect: "+0.8%", severity: "low",    c: C.green   },
    { asset: "TSLA", effect: "−4.3%", severity: "high",   c: "#F87171" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Article pill */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px" }}>
        <div style={{ fontFamily: C.font, fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.08em", color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Latest Article</div>
        <div style={{ fontFamily: C.font, fontSize: "0.68rem", fontWeight: 600, color: C.text, lineHeight: 1.4, marginBottom: 8 }}>
          "Fed signals higher-for-longer rates — markets react"
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
          background: "#F87171" + "15", border: "1px solid #F87171" + "35", borderRadius: 999,
        }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#F87171" }} />
          <span style={{ fontFamily: C.font, fontSize: "0.55rem", fontWeight: 700, color: "#F87171" }}>High Impact</span>
        </div>
      </div>
      {/* Portfolio impact */}
      <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px" }}>
        <div style={{ fontFamily: C.font, fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.08em", color: C.muted, textTransform: "uppercase", marginBottom: 7 }}>Your Portfolio Impact</div>
        {impacts.map(({ asset, effect, c }, i) => (
          <div key={asset} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "4px 0",
            borderBottom: i < impacts.length - 1 ? `1px solid ${C.border}` : "none",
            opacity: hov ? 1 : 0.4,
            transition: `opacity 0.3s ease ${i * 0.08}s`,
          }}>
            <span style={{ fontFamily: C.font, fontSize: "0.65rem", fontWeight: 600, color: C.text }}>{asset}</span>
            <span style={{ fontFamily: C.font, fontSize: "0.65rem", fontWeight: 800, color: c }}>{effect}</span>
          </div>
        ))}
        <div style={{ marginTop: 8, padding: "6px 8px", borderRadius: 7, background: `${C.green}10`, border: `1px solid ${C.green}25` }}>
          <span style={{ fontFamily: C.font, fontSize: "0.6rem", color: C.green }}>
            ✦ AI Action: Reduce rate-sensitive holdings by 8%
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Features section ──────────────────────────────────────────────────────────
function FeaturesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      number: "01",
      accent: C.green,
      title: "AI Powered Portfolio Generator",
      desc: "Statistically backtested portfolios engineered to generate positive alpha vs SPY over a 3–5 year horizon with an active management layer delivering ongoing rebalance and action recommendations.",
      mini: (h) => <PortfolioGenMini hov={h} />,
    },
    {
      number: "02",
      accent: "#60A5FA",
      title: "Brokerage Portfolio Integration",
      desc: "Import your existing portfolio directly from your brokerage. The platform analyses your current holdings against your strategy, identifies gaps, and constructs your ideal target portfolio.",
      mini: (h) => <BrokerageMini hov={h} />,
    },
    {
      number: "03",
      accent: C.purple,
      title: "AI Article Intelligence",
      desc: "After every article, the AI analyses how the news directly affects your portfolio quantifying exposure and generating specific actions to counteract risk or capture opportunity.",
      mini: (h) => <ArticleMini hov={h} />,
    },
  ];

  return (
    <section id="features" ref={ref} className="p3-features" style={{
      position: "relative", zIndex: 1,
      padding: "120px 48px 100px",
    }}>
      {/* Left border accent */}
      <div style={{
        position: "absolute", left: 0, top: "20%", bottom: "20%", width: 1,
        background: `linear-gradient(180deg, transparent, ${C.green}30, transparent)`,
      }} />

      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Header — left aligned with number decoration */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="p3-feat-header"
          style={{ marginBottom: 72, display: "flex", alignItems: "flex-end", gap: 40 }}
        >
          <div>
            <motion.div variants={fadeLeft} style={{
              fontFamily: C.font, fontSize: "0.68rem", fontWeight: 700,
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: C.green, marginBottom: 12,
            }}>
              Platform Features
            </motion.div>
            <motion.h2 variants={fadeLeft} style={{
              fontFamily: C.font,
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.12,
              color: C.text, margin: "0 0 14px",
            }}>
              Everything you need<br />
              <span style={{ fontWeight: 400, color: C.muted }}>to invest smarter</span>
            </motion.h2>
            <motion.p variants={fadeLeft} style={{
              fontFamily: C.font, fontSize: "1rem", color: C.muted,
              lineHeight: 1.72, maxWidth: 420, margin: 0,
            }}>
              Three core pillars that give you an institutional edge —
              without the institutional price tag.
            </motion.p>
          </div>

          {/* Decorative number */}
          <div className="p3-feat-deco" style={{
            fontFamily: C.font, fontSize: "clamp(5rem, 9vw, 9rem)",
            fontWeight: 900, letterSpacing: "-0.06em", lineHeight: 1,
            color: "rgba(16,183,127,0.05)", flexShrink: 0,
            userSelect: "none",
          }}>
            
          </div>
        </motion.div>

        {/* Cards — 3 col, slightly different row heights via padding */}
        <div className="p3-feat-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 18,
          alignItems: "start",
        }}>
          {features.map((f, i) => (
            <FeatureCard key={f.title} {...f} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why join section ──────────────────────────────────────────────────────────
const WHY_ITEMS = [
  { icon: "🚀", title: "Priority Access",      desc: "Waitlist members get 15 days pre launch free trial of the plus features." },
  { icon: "🎓", title: "Shape the Product",    desc: "Provide early feedback for the products features, roadmap and the direction of the platform." },
  { icon: "💎", title: "Founding Member Perks", desc: "Exclusive benefits, premium tier access, and recognition as a founding member of Alpha Stack." },
  { icon: "📰", title: "Weekly Market Brief", desc: "Access to emailed market briefs." },
];

const WHY_ACCENTS = [C.green, "#627EEA", "#F7931A", "#E879F9"];
const WHY_SVGS = [
  // Rocket / priority
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 3C14 3 20 7 20 14c0 3.3-2.7 6-6 6s-6-2.7-6-6c0-7 6-11 6-11z" fill="currentColor" opacity="0.18"/><path d="M14 3C14 3 20 7 20 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 14c0 3.3 2.7 6 6 6s6-2.7 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="14" cy="13" r="2.5" fill="currentColor"/><path d="M11 20l-2 4M17 20l2 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  // Shape / feedback
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="4" y="4" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M9 24l5-6 5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 10h10M9 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  // Diamond / perks
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 4L24 11l-10 13L4 11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" opacity="0.12"/><path d="M4 11h20M14 4L8 11l6 13 6-13-6-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  // Newsletter / brief
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="3" y="6" width="22" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 10l11 7 11-7" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
];

function WhySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="why" ref={ref} className="p3-why" style={{
      position: "relative", zIndex: 1,
      padding: "130px 48px",
      borderTop: `1px solid ${C.border}`,
    }}>
      {/* Multi-color radial glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: [
          `radial-gradient(ellipse 55% 40% at 20% 60%, ${C.green}0A, transparent 60%)`,
          `radial-gradient(ellipse 55% 40% at 80% 30%, #627EEA0A, transparent 60%)`,
          `radial-gradient(ellipse 40% 30% at 50% 100%, #F7931A06, transparent 55%)`,
        ].join(", "),
      }} />

      <div style={{ maxWidth: 1120, margin: "0 auto", position: "relative" }}>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
          style={{ textAlign: "center", marginBottom: 88 }}
        >
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "7px 18px",
            background: `${C.green}14`,
            border: `1px solid ${C.green}30`,
            borderRadius: 999, marginBottom: 24,
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%", background: C.green,
              display: "inline-block", boxShadow: `0 0 8px ${C.green}`,
            }} />
            <span style={{ fontFamily: C.font, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: C.green }}>
              Why Join Early
            </span>
          </div>
          <h2 style={{
            fontFamily: C.font,
            fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
            fontWeight: 900, letterSpacing: "-0.045em", lineHeight: 1.0,
            color: C.text, margin: "0 0 20px",
          }}>
            Get ahead before{" "}
            <span style={{
              background: `linear-gradient(135deg, ${C.green} 0%, #6EE7B7 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>the crowd.</span>
          </h2>
          <p style={{
            fontFamily: C.font, fontSize: "1.08rem", color: "rgba(238,240,246,0.6)",
            lineHeight: 1.72, maxWidth: 500, margin: "0 auto",
          }}>
            The waitlist is your ticket to founding-member status and exclusive early access perks.
          </p>
        </motion.div>

        {/* 2×2 grid */}
        <div className="p3-why-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 20,
        }}>
          {WHY_ITEMS.map((item, i) => (
            <WhyCard key={item.title} item={item} i={i} inView={inView} accent={WHY_ACCENTS[i]} svg={WHY_SVGS[i]} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCard({ item, i, inView, accent, svg }) {
  const [hov, setHov] = useState(false);
  const num = String(i + 1).padStart(2, "0");

  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.08 + i * 0.13, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: hov
          ? `linear-gradient(135deg, ${accent}0D 0%, rgba(255,255,255,0.03) 100%)`
          : "rgba(255,255,255,0.025)",
        border: `1px solid ${hov ? accent + "55" : "rgba(255,255,255,0.07)"}`,
        borderRadius: 28, padding: "40px 40px 36px",
        overflow: "hidden",
        transform: hov ? "translateY(-8px) scale(1.01)" : "translateY(0) scale(1)",
        boxShadow: hov
          ? `0 32px 80px ${accent}22, 0 0 0 1px ${accent}18, inset 0 1px 0 rgba(255,255,255,0.08)`
          : `0 4px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)`,
        transition: "all 0.42s cubic-bezier(0.16,1,0.3,1)",
        cursor: "default",
      }}
    >
      {/* Top accent bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: hov
          ? `linear-gradient(90deg, transparent 0%, ${accent} 40%, ${accent}80 100%)`
          : `linear-gradient(90deg, transparent 0%, ${accent}40 40%, transparent 100%)`,
        transition: "all 0.42s ease",
        borderRadius: "28px 28px 0 0",
      }} />

      {/* Corner glow */}
      <div style={{
        position: "absolute", top: -40, right: -40,
        width: 160, height: 160, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}${hov ? "18" : "08"}, transparent 70%)`,
        transition: "all 0.42s ease",
        pointerEvents: "none",
      }} />

      {/* Ghost number */}
      <div style={{
        position: "absolute", bottom: -10, right: 16,
        fontFamily: C.font, fontSize: "8rem", fontWeight: 900,
        color: `${accent}${hov ? "14" : "08"}`, lineHeight: 1,
        userSelect: "none", pointerEvents: "none",
        letterSpacing: "-0.06em",
        transition: "color 0.42s ease",
      }}>
        {num}
      </div>

      {/* Icon block */}
      <div style={{
        width: 56, height: 56, borderRadius: 16,
        background: `${accent}16`,
        border: `1px solid ${accent}${hov ? "45" : "28"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: accent,
        marginBottom: 24,
        boxShadow: hov ? `0 0 24px ${accent}30` : "none",
        transition: "all 0.42s ease",
      }}>
        <motion.div
          animate={{ scale: hov ? 1.12 : 1, rotate: hov ? -5 : 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          {svg}
        </motion.div>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: C.font, fontSize: "1.15rem", fontWeight: 800,
        color: C.text, margin: "0 0 10px", letterSpacing: "-0.025em", lineHeight: 1.2,
      }}>
        {item.title}
      </h3>

      {/* Animated divider */}
      <motion.div
        animate={{ width: hov ? 72 : 40 }}
        transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
        style={{
          height: 2, borderRadius: 2, marginBottom: 16,
          background: `linear-gradient(90deg, ${accent}, transparent)`,
        }}
      />

      {/* Description */}
      <p style={{
        fontFamily: C.font, fontSize: "0.92rem", color: "rgba(238,240,246,0.78)",
        lineHeight: 1.72, margin: 0,
      }}>
        {item.desc}
      </p>
    </motion.div>
  );
}

// ── Stats row ─────────────────────────────────────────────────────────────────
function StatsRow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  const stats = [
    { value: "+18.4%",             label: "Excess Return VS S&P 500"    },
    { value: "25 Year",           label: "Backtested horizon"  },
    { value: "3+",                label: "Portfolio frameworks" },
    { value: "2008 · 2020 · 2022", label: "Stress tested"       },
  ];

  return (
    <section ref={ref} className="p3-stats" style={{
      position: "relative", zIndex: 1,
      padding: "64px 48px",
      borderTop:    `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
      background: "rgba(0,0,0,0.3)",
    }}>
      <div className="p3-stats-grid" style={{
        maxWidth: 1100, margin: "0 auto",
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
      }}>
        {stats.map(({ value, label }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.1, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="p3-stat-item"
            style={{
              padding: "32px 28px", textAlign: "center",
              borderRight: i < stats.length - 1 ? `1px solid ${C.border}` : "none",
            }}
          >
            <div style={{
              fontFamily: C.font,
              fontSize: "clamp(1rem, 1.8vw, 1.5rem)",
              fontWeight: 900, letterSpacing: "-0.02em",
              color: C.green, marginBottom: 8, lineHeight: 1.2,
            }}>
              {value}
            </div>
            <div style={{
              fontFamily: C.font, fontSize: "0.82rem", color: C.muted,
            }}>
              {label}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

// ── Quote section ─────────────────────────────────────────────────────────────
function QuoteSection() {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [imgErr, setImgErr] = useState(false);

  const pills = ["Beat S&P 500 YOY", "8-Year Track Record", "Statistically Engineered"];

  return (
    <section ref={ref} className="p3-quote" style={{
      position: "relative", zIndex: 1,
      padding: "100px 48px",
      borderTop: `1px solid ${C.border}`,
    }}>
      {/* Background accent glow */}
      <div style={{
        position: "absolute", top: 0, left: "50%",
        transform: "translateX(-50%)",
        width: 900, height: 400, pointerEvents: "none",
        background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${C.green}07, transparent 70%)`,
        filter: "blur(30px)",
      }} />

      <div style={{ maxWidth: 980, margin: "0 auto", position: "relative" }}>

        {/* Section label */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{
            fontFamily: C.font, fontSize: "0.68rem", fontWeight: 700,
            letterSpacing: "0.18em", textTransform: "uppercase",
            color: C.green, marginBottom: 40, textAlign: "center",
          }}
        >
          Built by an Investor Who Beat the Market
        </motion.div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="quote-card-row"
          style={{
            background: "rgba(255,255,255,0.022)",
            border: `1px solid ${C.border}`,
            borderRadius: 28,
            overflow: "hidden",
            position: "relative",
            display: "flex",
            boxShadow: `0 40px 120px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)`,
          }}
        >
          {/* Top edge glow line */}
          <div style={{
            position: "absolute", top: 0, left: "10%", right: "10%", height: 1,
            background: `linear-gradient(90deg, transparent, ${C.green}40, transparent)`,
          }} />

          {/* Left: avatar column */}
          <div className="p3-quote-left" style={{
            flexShrink: 0, width: 260,
            borderRight: `1px solid ${C.border}`,
            background: "rgba(0,0,0,0.22)",
            padding: "52px 36px",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 18,
          }}>
            {/* Photo */}
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={inView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
              style={{
                width: 104, height: 104, borderRadius: "50%",
                border: `2px solid ${C.green}40`,
                boxShadow: `0 0 0 6px ${C.green}10, 0 0 40px ${C.green}25`,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {!imgErr ? (
                <img
                  src="/images/founder.webp"
                  alt="Founder"
                  onError={() => setImgErr(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  background: `linear-gradient(135deg, ${C.green}40, ${C.blue}30)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: C.font, fontSize: "2rem", fontWeight: 900, color: C.green,
                }}>AS</div>
              )}
            </motion.div>

            {/* Name */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.35, duration: 0.55 }}
              style={{ textAlign: "center" }}
            >
              <div style={{
                fontFamily: C.font, fontSize: "0.88rem", fontWeight: 800,
                letterSpacing: "0.06em", textTransform: "uppercase",
                color: C.text, marginBottom: 5,
              }}>
                The Founder
              </div>
              <div style={{
                fontFamily: C.font, fontSize: "0.72rem", fontWeight: 500,
                color: C.muted, letterSpacing: "0.01em",
              }}>
                Founder & CEO, AlphaStack
              </div>
            </motion.div>

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.45, duration: 0.5 }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 12px",
                background: `${C.green}12`,
                border: `1px solid ${C.green}30`,
                borderRadius: 999,
              }}
            >
              <span style={{ color: C.green, fontSize: "0.62rem" }}>✦</span>
              <span style={{
                fontFamily: C.font, fontSize: "0.62rem", fontWeight: 700,
                letterSpacing: "0.12em", textTransform: "uppercase", color: C.green,
              }}>
                Verified Track Record
              </span>
            </motion.div>
          </div>

          {/* Right: quote content */}
          <div className="p3-quote-right" style={{ flex: 1, padding: "52px 56px 48px" }}>

            {/* Decorative large quote marks */}
            <div style={{
              fontFamily: "Georgia, serif",
              fontSize: "7rem", lineHeight: 0.7,
              color: `${C.green}18`,
              marginBottom: 24,
              letterSpacing: "-0.05em",
              userSelect: "none",
            }}>
              "
            </div>

            {/* Quote text */}
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              style={{
                margin: "0 0 36px",
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: "clamp(1.15rem, 2.2vw, 1.55rem)",
                fontWeight: 400,
                fontStyle: "italic",
                color: C.text,
                lineHeight: 1.62,
                letterSpacing: "0.005em",
              }}
            >
              Over an{" "}
              <em style={{ fontStyle: "normal", fontWeight: 700, color: C.green }}>
                8-year period
              </em>
              , the frameworks powering AlphaStack have{" "}
              <em style={{ fontStyle: "normal", fontWeight: 700, color: C.green }}>
                beaten the S&P 500 year-over-year
              </em>
              . Portfolios built on statistically engineered signals are{" "}
              <em style={{
                fontStyle: "normal", fontWeight: 700,
                background: `linear-gradient(135deg, ${C.green}, #6EE7B7)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                designed to generate positive alpha — across every market cycle.
              </em>
            </motion.blockquote>

            {/* Divider */}
            <div style={{
              width: 48, height: 1,
              background: `linear-gradient(90deg, ${C.green}60, transparent)`,
              marginBottom: 24,
            }} />

            {/* Pill tags */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
            >
              {pills.map((p, i) => (
                <motion.span
                  key={p}
                  initial={{ opacity: 0, x: -10 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.52 + i * 0.08, duration: 0.45 }}
                  style={{
                    fontFamily: C.font, fontSize: "0.72rem", fontWeight: 600,
                    padding: "5px 14px", borderRadius: 999,
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${C.border}`,
                    color: "rgba(238,240,246,0.55)",
                    letterSpacing: "0.02em",
                  }}
                >
                  {p}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── CTA section ───────────────────────────────────────────────────────────────
function CTASection({ waitlistCount = "1,200+" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="p3-cta" ref={ref} className="p3-cta" style={{
      position: "relative", zIndex: 1,
      padding: "140px 48px 120px",
      overflow: "hidden",
    }}>
      {/* Glow */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 900, height: 600, pointerEvents: "none",
        background: `radial-gradient(ellipse, ${C.green}09 0%, transparent 70%)`,
        filter: "blur(20px)",
      }} />

      {/* Grid lines */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          `linear-gradient(${C.green}05 1px, transparent 1px),` +
          `linear-gradient(90deg, ${C.green}05 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
        maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 100%)",
      }} />

      <div style={{
        maxWidth: 620, margin: "0 auto",
        textAlign: "center", position: "relative",
      }}>
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Avatar stack */}
          <motion.div variants={fadeUp} style={{
            display: "flex", justifyContent: "center",
            alignItems: "center", gap: 12, marginBottom: 36,
          }}>
            <div style={{ display: "flex" }}>
              {[C.green, "#60A5FA", C.purple, C.gold, "#F87171"].map((c, i) => (
                <div key={c} style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${c}55, ${c}18)`,
                  border: "2.5px solid #04060E",
                  marginLeft: i > 0 ? -10 : 0,
                }} />
              ))}
            </div>
            <span style={{
              fontFamily: C.font, fontSize: "0.82rem",
              color: "rgba(238,240,246,0.4)",
            }}>
              <strong style={{ color: "rgba(238,240,246,0.8)", fontWeight: 700 }}>{waitlistCount}</strong> investors already joined
            </span>
          </motion.div>

          <motion.div variants={fadeUp} style={{
            fontFamily: C.font, fontSize: "0.68rem", fontWeight: 700,
            letterSpacing: "0.18em", textTransform: "uppercase",
            color: C.green, marginBottom: 16,
          }}>
            Get Early Access
          </motion.div>

          <motion.h2 variants={fadeUp} style={{
            fontFamily: C.font,
            fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)",
            fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.1,
            color: C.text, margin: "0 0 18px",
          }}>
            Don't invest<br />
            <span style={{ fontWeight: 400, color: C.muted }}>in the dark.</span>
          </motion.h2>

          <motion.p variants={fadeUp} style={{
            fontFamily: C.font, fontSize: "1rem", color: C.muted,
            lineHeight: 1.75, maxWidth: 380, margin: "0 auto 44px",
          }}>
            Join {waitlistCount} investors already on the waitlist.
            Secure your spot before we go public.
          </motion.p>

          <motion.div variants={fadeUp} style={{ maxWidth: 460, margin: "0 auto 16px" }}>
            <WaitlistForm variant="cta" />
          </motion.div>

          <motion.p variants={fadeUp} style={{
            fontFamily: C.font, fontSize: "0.72rem",
            color: "rgba(238,240,246,0.22)",
          }}>
            Free to join · No credit card required
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="p3-footer" style={{
      position: "relative", zIndex: 1,
      borderTop: `1px solid ${C.border}`,
      padding: "40px 48px",
      background: "rgba(0,0,0,0.4)",
    }}>
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src="/images/alphastack-logo-pVgok4VY (1).png"
            alt="AlphaStack"
            style={{ height: 30, objectFit: "contain" }}
          />
        </div>

        <p style={{
          fontFamily: C.font, fontSize: "0.76rem", color: "rgba(238,240,246,0.25)",
          margin: 0,
        }}>
          © 2026 AlphaStack. All rights reserved.
        </p>

        <div style={{ display: "flex", gap: 24 }}>
          {["Privacy", "Terms", "Contact"].map((t) => (
            <span key={t} style={{
              fontFamily: C.font, fontSize: "0.76rem", color: "rgba(238,240,246,0.35)",
              cursor: "pointer",
            }}>{t}</span>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PreviewPage3() {
  const [loaded, setLoaded] = useState(false);
  const lenisRef = useRef(null);
  const waitlistCount = useWaitlistCount();

  const handleLoaded = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    if (!loaded) return;

    // ── Lenis smooth scroll ──────────────────────────────
    const lenis = new Lenis({ lerp: 0.075 });
    lenisRef.current = lenis;

    // Sync Lenis with GSAP ticker
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // ── Track scroll progress for 3D camera ─────────────
    const onScroll = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      scrollState.progress = max > 0 ? window.scrollY / max : 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // ── GSAP section animations ──────────────────────────
    // Parallax on hero headline
    gsap.to(".p3-headline", {
      yPercent: -18,
      ease: "none",
      scrollTrigger: {
        trigger: ".p3-hero",
        start: "top top",
        end: "bottom top",
        scrub: 1.2,
      },
    });

    // Features section horizontal line reveal
    gsap.fromTo(".p3-feat-line", { scaleX: 0 }, {
      scaleX: 1,
      ease: "power2.out",
      scrollTrigger: {
        trigger: "#features",
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove((time) => lenis.raf(time * 1000));
      window.removeEventListener("scroll", onScroll);
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [loaded]);

  return (
    <div style={{
      background: C.bg,
      minHeight: "100vh",
      overflowX: "hidden",
    }}>
      {/* Loading overlay */}
      <AnimatePresence mode="wait">
        {!loaded && <LoadingScreen key="loader" onDone={handleLoaded} />}
      </AnimatePresence>

      {/* 3D Canvas — fixed behind everything */}
      <Scene3D />

      {/* Page content */}
      <div style={{ position: "relative", zIndex: 1 }}>
        {loaded && (
          <>
            <Navbar />
            <main>
              <div className="p3-hero">
                <HeroSection />
              </div>
              <FeaturesSection />
              <WhySection />
              <StatsRow />
              <QuoteSection />
              <CTASection waitlistCount={waitlistCount} />
            </main>
            <Footer />
          </>
        )}
      </div>
    </div>
  );
}
